import * as stylex from "@stylexjs/stylex";
import React from "react";
import { colors } from "../materials/consts.stylex";
import type { FigureHeight } from "./figure";

const LoadingIcon = new URL("../assets/ic_brandmark_40dp.svg", import.meta.url).href;

// The `body[data-ready] &` visibility toggle (hiding the global spinner once
// the app is ready) is an ancestor-scoped selector StyleX can't express —
// it's carved out as a plain CSS rule in `style.global.css` targeting
// `#global-spinner` instead.
const globalSpinnerContainerKeyframes = stylex.keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const gemSpinnerKeyframes = stylex.keyframes({
  to: { transform: "rotate(720deg)" },
});

const styles = stylex.create({
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    color: colors.lightText,
  },
  global: {
    position: "absolute",
    opacity: 0,
    animationName: globalSpinnerContainerKeyframes,
    animationDuration: "1s",
    animationTimingFunction: "cubic-bezier(1, 0, 1, 0)",
    animationDelay: "2s",
    animationFillMode: "forwards",
  },
  spinner: {
    animationName: gemSpinnerKeyframes,
    animationDuration: "4s",
    animationTimingFunction: "cubic-bezier(0.19, 0.26, 0.14, 0.94)",
    animationIterationCount: "infinite",
    width: "40px",
    height: "40px",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center center",
    marginBottom: "8px",
  },
});

export const Spinner = ({ global, minHeight }: { global?: boolean; minHeight?: FigureHeight }) => {
  return (
    <div
      {...stylex.props(styles.root, global && styles.global)}
      id={global ? "global-spinner" : undefined}
      style={{
        minHeight: minHeight?.wide /** @FIXME handle mobile sizes if needed  */,
      }}
    >
      <div {...stylex.props(styles.spinner)} style={{ backgroundImage: `url(${LoadingIcon})` }} />

      {/* Can not use react-intl because this may be rendered outside of <IntlProvider> */}
      <span>SCOPE</span>
    </div>
  );
};

/**
 * The GlobalSpinner is used together with the HideGlobalSpinner component.
 * The intention is to render the GlobalSpinner in the server-side rendered
 * HTML (i.e. in _document.tsx) and to hide it as soon as the application
 * is through with loading the config data that it needs.
 *
 * The "signal" on when loading is done happens in _app.tsx where the hiding
 * component is rendered as soon as all the configs have been loaded.
 */

export const GlobalSpinner = () => {
  return <Spinner global />;
};

export const HideGlobalSpinner = () => {
  React.useEffect(() => {
    const body = document.querySelector("body");
    if (body) body.setAttribute("data-ready", "");
  });
  return null;
};
